import React from 'react';
import { unwrapNestedValue } from './formVisibility';

/**
 * normalizeRawAnswer
 * ------------------
 * Merges the legacy sibling `otherAnswer` field into the modern
 * `{ value, otherValue }` format so that downstream consumers
 * (FormattedAnswer, option-highlighting, etc.) work uniformly regardless
 * of which storage generation the patient's data uses.
 *
 * Legacy format (demo patients, possibly old production data):
 *   subquestion.answer      = ['Herbal supplements or vitamins']
 *   subquestion.otherAnswer  = 'My wife has me on pre-natal supplements'
 *                            — OR —
 *   subquestion.otherAnswer  = { 'Autism': 'Two sisters…', 'ADHD': 'Brother…' }
 *
 * Modern format (current PatientFormTemplate saves):
 *   subquestion.answer = [{ value: 'Herbal supplements or vitamins',
 *                           otherValue: 'My wife has me on pre-natal supplements' }]
 *
 * When otherAnswer is falsy the answer is returned unchanged, so calling this
 * function is always safe — it's a no-op for modern data.
 *
 * @param {*} answer      - subquestion.answer (string | object | array | null)
 * @param {*} otherAnswer - subquestion.otherAnswer (string | object | null/undefined)
 * @returns {*} The answer in modern format with otherValue merged in where applicable
 */
export const normalizeRawAnswer = (answer, otherAnswer) => {
  if (!otherAnswer || answer == null) return answer;

  // answer is an array (selectMultiple legacy: plain string items)
  if (Array.isArray(answer)) {
    return answer.map((item) => {
      // Already a structured object (modern format) — keep as-is unless
      // it's missing otherValue while a legacy otherAnswer has free text for it
      if (typeof item === 'object' && item !== null) {
        if (!item.otherValue && item.value && otherAnswer) {
          const freeText = typeof otherAnswer === 'string'
            ? (answer.length === 1 ? otherAnswer : undefined)
            : (typeof otherAnswer === 'object' ? otherAnswer[item.value] : undefined);
          if (freeText) return { ...item, otherValue: freeText };
        }
        return item;
      }

      // Plain string item — look up free text from otherAnswer
      const freeText = typeof otherAnswer === 'string'
        // String otherAnswer: applies when the array has a single item
        ? (answer.length === 1 ? otherAnswer : undefined)
        // Dict otherAnswer: look up by option label
        : (typeof otherAnswer === 'object' ? otherAnswer[item] : undefined);

      return freeText ? { value: item, otherValue: freeText } : { value: item };
    });
  }

  // answer is a plain string (selectOne legacy)
  if (typeof answer === 'string') {
    const freeText = typeof otherAnswer === 'string'
      ? otherAnswer
      : (typeof otherAnswer === 'object' ? otherAnswer[answer] : undefined);

    return freeText ? { value: answer, otherValue: freeText } : { value: answer };
  }

  // answer is already a structured object with .value (modern format).
  // If it's missing otherValue but a legacy otherAnswer exists, merge it in
  // so downstream consumers see the free text regardless of storage generation.
  if (typeof answer === 'object' && answer !== null && answer.value && !answer.otherValue && otherAnswer) {
    const freeText = typeof otherAnswer === 'string'
      ? otherAnswer
      : (typeof otherAnswer === 'object' ? otherAnswer[answer.value] : undefined);
    if (freeText) {
      return { ...answer, otherValue: freeText };
    }
  }

  return answer;
};

/**
 * buildDisplayAnswer
 * ------------------
 * Converts a normalized answer value (output of normalizeRawAnswer) into a
 * plain-text string suitable for display in dialogs and summary panels.
 *
 * Handles all answer shapes:
 *  - string       → returned as-is
 *  - array        → bullet-point list, each item formatted as "value: otherValue"
 *  - object       → "value: otherValue" or just "value"
 *  - null/empty   → emptyLabel fallback
 *
 * @param {*}      normalizedAnswer - The answer after normalizeRawAnswer processing
 * @param {string} [emptyLabel="No answer given"] - Text to show when the answer is empty
 * @returns {string} A plain-text display string
 */
export const buildDisplayAnswer = (normalizedAnswer, emptyLabel = 'No answer given') => {
  const hasAnswer = normalizedAnswer !== null && normalizedAnswer !== undefined && normalizedAnswer !== '';

  if (!hasAnswer) return emptyLabel;

  if (typeof normalizedAnswer === 'string') {
    return normalizedAnswer;
  }

  if (Array.isArray(normalizedAnswer)) {
    return normalizedAnswer.map((item) => {
      let itemText;
      if (typeof item === 'object' && item !== null && item.value) {
        // Unwrap nested {value: {value: ...}} to the leaf string so corrupted
        // data renders the option label instead of "[object Object]".
        const leafValue = unwrapNestedValue(item.value) ?? item.value;
        itemText = item.otherValue ? `${leafValue}: ${item.otherValue}` : String(leafValue);
      } else {
        itemText = typeof item === 'object' ? JSON.stringify(item) : item;
      }
      return `• ${itemText}`;
    }).join('\n');
  }

  if (typeof normalizedAnswer === 'object' && normalizedAnswer !== null) {
    if (normalizedAnswer.value) {
      const leafValue = unwrapNestedValue(normalizedAnswer.value) ?? normalizedAnswer.value;
      return normalizedAnswer.otherValue
        ? `${leafValue}: ${normalizedAnswer.otherValue}`
        : String(leafValue);
    }
    return JSON.stringify(normalizedAnswer);
  }

  return String(normalizedAnswer);
};

/**
 * coerceDisplayText
 * -----------------
 * Ensures a value destined for FormattedAnswer.displayText is a plain string.
 * History prefill fields are usually strings, but legacy or malformed Firestore
 * data may store arrays/objects (e.g. selectMultiple answers written directly
 * to patientPrefill). Without coercion, FormattedAnswer's recovery heuristics
 * call .match() and throw.
 *
 * @param {*} value - displayText candidate (string, array, object, null, etc.)
 * @returns {string|null} Plain-text display string, or null when empty
 */
export function coerceDisplayText(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') return value;
  const formatted = buildDisplayAnswer(value, '');
  return formatted || null;
}

/**
 * renderFormattedAnswerItem
 * ------------------------
 * Renders a single answer item (object with value/otherValue) as JSX.
 * The option label is shown normally; if otherValue is present and non-empty,
 * it is appended in italic curly-quoted text so clinicians can immediately
 * distinguish the patient's free-text elaboration from the option label.
 *
 * @param {Object} item - An answer object with { value, otherValue? }
 * @returns {React.ReactNode}
 */
const renderFormattedAnswerItem = (item) => {
  if (typeof item !== 'object' || item === null || !item.value) {
    return typeof item === 'string' ? item : String(item ?? '');
  }

  // Unwrap nested {value: {value: ...}} to the leaf string so corrupted
  // data renders the option label instead of "[object Object]".
  const leafValue = unwrapNestedValue(item.value) ?? item.value;

  const trimmedOther = item.otherValue != null
    ? String(item.otherValue).trim()
    : '';

  if (trimmedOther.length === 0) {
    return String(leafValue);
  }

  return (
    <>
      {String(leafValue)}:{' '}
      <span className="italic">&ldquo;{trimmedOther}&rdquo;</span>
    </>
  );
};

/**
 * tryRecoverFreeText
 * ------------------
 * Attempts to recover free-text details from a displayText string for items
 * that are missing their `otherValue`. Uses regex matching against the
 * common "option: free text" format that PrefillFunctions and
 * buildDisplayAnswer produce.
 *
 * Works for both plain string items and structured { value } objects that
 * lost their otherValue (e.g. due to a stale Firestore write, v2
 * FormTemplate bug, or legacy data migration).
 *
 * @param {string} valueStr - The option label to search for in displayText
 * @param {string} displayText - The full display string to extract from
 * @returns {string|null} The recovered free text, or null if none found
 */
const tryRecoverFreeText = (valueStr, displayText) => {
  const displayStr = coerceDisplayText(displayText);
  if (!displayStr || !valueStr) return null;
  const escapedOption = valueStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Capture everything after "Option: " up to the next bullet point ("• "),
  // the next "Label: " heading on a new line, or end-of-string.  This allows
  // multi-line free text (e.g. paragraphs the patient typed) to be recovered
  // in full rather than truncating at the first newline.
  const regex = new RegExp(
    `${escapedOption}:\\s*([\\s\\S]+?)(?=\\n•|\\n[^\\s].*:|$)`
  );
  const match = displayStr.match(regex);
  return match && match[1]?.trim() ? match[1].trim() : null;
};

/**
 * Flatten a rawAnswer (single object or array) into {value, otherValue} items
 * that actually carry typed free text.
 */
const collectFreeTextItems = (rawAnswer) => {
  if (!rawAnswer || typeof rawAnswer !== 'object') return [];
  const list = Array.isArray(rawAnswer) ? rawAnswer : [rawAnswer];
  return list.filter((item) => {
    if (!item || typeof item !== 'object' || item.value == null) return false;
    const other = item.otherValue != null ? String(item.otherValue).trim() : '';
    return other.length > 0;
  });
};

/**
 * For composite prefill strings (e.g. Partnership: labelled lines plus
 * "Yes: typed detail"), inject italic curly quotes around the typed detail
 * *inside* the existing displayText — without dropping the surrounding labels.
 *
 * Returns null when displayText is just the simple "option: detail" form (caller
 * should use renderFormattedAnswerItem), or when no needle is found.
 */
const renderDisplayTextWithItalicFreeText = (displayText, rawAnswer) => {
  const freeTextItems = collectFreeTextItems(rawAnswer);
  if (!displayText || freeTextItems.length === 0) return null;

  // Single-item, non-composite display — keep the existing simple renderer so
  // we don't change ADHD CI / single-source History behaviour.
  if (freeTextItems.length === 1) {
    const simple = buildDisplayAnswer(freeTextItems[0], '');
    if (
      displayText === simple
      || displayText === `• ${simple}`
      || displayText === freeTextItems[0].otherValue
    ) {
      return null;
    }
  }

  // Only rewrite labelled composite prefills where the option:detail pair sits
  // after a label on the same line (e.g. "Relationship challenges: Yes: …").
  // Multi-option lists like "Autism: …\nADHD: …" have empty prefixes before each
  // option and must keep the bullet renderer.
  const matches = [];
  freeTextItems.forEach((item) => {
    const leafValue = unwrapNestedValue(item.value) ?? item.value;
    const other = String(item.otherValue).trim();
    const needle = `${leafValue}: ${other}`;
    let from = 0;
    let pos = displayText.indexOf(needle, from);
    while (pos !== -1) {
      const lineStart = displayText.lastIndexOf('\n', pos - 1) + 1;
      const prefix = displayText.slice(lineStart, pos);
      // Require a same-line label that ends with ":" (e.g. "Relationship
      // challenges: "). Reject bare multi-option lists where the option is at
      // the start of the line, and reject false prefixes that only exist
      // because a literal "\n" kept two options on one logical line.
      if (/:\s*$/.test(prefix)) {
        matches.push({
          start: pos,
          end: pos + needle.length,
          leafValue: String(leafValue),
          other,
        });
      }
      from = pos + needle.length;
      pos = displayText.indexOf(needle, from);
    }
  });

  if (matches.length === 0) return null;

  matches.sort((a, b) => a.start - b.start);
  const nonOverlap = [];
  let lastEnd = -1;
  matches.forEach((m) => {
    if (m.start >= lastEnd) {
      nonOverlap.push(m);
      lastEnd = m.end;
    }
  });

  const nodes = [];
  let cursor = 0;
  nonOverlap.forEach((m, i) => {
    if (m.start > cursor) {
      nodes.push(displayText.slice(cursor, m.start));
    }
    nodes.push(
      <React.Fragment key={`ft-${i}-${m.start}`}>
        {m.leafValue}:{' '}
        <span className="italic">&ldquo;{m.other}&rdquo;</span>
      </React.Fragment>
    );
    cursor = m.end;
  });
  if (cursor < displayText.length) {
    nodes.push(displayText.slice(cursor));
  }

  return <>{nodes}</>;
};

/**
 * FormattedAnswer
 * ---------------
 * Renders a form answer with inline formatting for free-text follow-ups.
 *
 * When the raw answer contains structured objects with `otherValue` (the
 * free text a patient typed after selecting an option like "Other: please
 * provide details"), the free text is rendered in italic curly quotes
 * directly inline -- e.g. `Other: \u201cI have chronic migraines\u201d`.
 *
 * Recovery heuristic: if the structured data has lost its `otherValue`
 * (e.g. stale data, v2 FormTemplate bug, legacy migration), but the
 * displayText string still contains the "option: free text" pattern
 * (typically from a patientPrefill string baked in by PrefillFunctions),
 * the component attempts to recover and re-format the free text. This
 * ensures consistent display regardless of minor data inconsistencies.
 *
 * Composite History prefills (e.g. Partnership) may pass a free-text-bearing
 * rawAnswer together with a longer labelled displayText. In that case the
 * typed detail is italic-quoted *inside* the labelled string so surrounding
 * context is preserved.
 *
 * @param {{ rawAnswer: *, displayText: string }} props
 *   rawAnswer   - the raw form answer (same object stored as item.rawAnswer)
 *   displayText - the pre-formatted plain-text answer (same string as item.answer)
 */
export const FormattedAnswer = ({ rawAnswer, displayText }) => {
  const safeDisplayText = coerceDisplayText(displayText);

  // No structured answer data -- fall back to the plain display string
  if (rawAnswer === null || rawAnswer === undefined || rawAnswer === '') {
    return <>{safeDisplayText}</>;
  }

  // Plain string answers (freeText, freeTextArea, selectOne labels) — no quoting
  if (typeof rawAnswer !== 'object') {
    return <>{safeDisplayText}</>;
  }

  // Single object (selectOne): { value: "Other", otherValue: "details" }
  if (!Array.isArray(rawAnswer)) {
    if (rawAnswer.otherValue) {
      // Composite History prefills (e.g. Partnership) pass a free-text-bearing
      // item PLUS a longer labelled displayText. Inject italics into that
      // string so surrounding labels are preserved.
      const injected = renderDisplayTextWithItalicFreeText(safeDisplayText, rawAnswer);
      if (injected) return injected;
      return <>{renderFormattedAnswerItem(rawAnswer)}</>;
    }
    // otherValue is missing — try to recover from displayText in case the
    // structured data lost its free text but the display string still has it
    const recovered = tryRecoverFreeText(rawAnswer.value, safeDisplayText);
    if (recovered) {
      const recoveredAnswer = { ...rawAnswer, otherValue: recovered };
      const injected = renderDisplayTextWithItalicFreeText(safeDisplayText, recoveredAnswer);
      if (injected) return injected;
      return <>{renderFormattedAnswerItem(recoveredAnswer)}</>;
    }
    return <>{renderFormattedAnswerItem(rawAnswer)}</>;
  }

  // Array (selectMultiple): render each item as a bullet, with formatted
  // free text on items that have otherValue
  if (rawAnswer.length === 0) {
    return <>{safeDisplayText}</>;
  }

  // Normalise items into { value, otherValue? } objects regardless of whether
  // the array contains plain strings, structured objects, or a mix. After
  // normalisation, attempt to recover any missing otherValue from displayText.
  const normalizedItems = rawAnswer.map((item) => {
    if (typeof item === 'object' && item !== null && item.value) {
      return item;
    }
    if (typeof item === 'string') {
      return { value: item };
    }
    return item;
  });

  const allNormalized = normalizedItems.every(
    (item) => typeof item === 'object' && item !== null && item.value
  );

  if (allNormalized && safeDisplayText) {
    // Try recovering missing otherValue from displayText on a per-item basis.
    // Items that already have otherValue are kept intact so we never overwrite
    // the authoritative structured data with a stale display string.
    const enriched = normalizedItems.map((item) => {
      if (item.otherValue) return item;
      const recovered = tryRecoverFreeText(item.value, safeDisplayText);
      return recovered ? { ...item, otherValue: recovered } : item;
    });

    const hasAnyFreeText = enriched.some((item) => item.otherValue);

    if (hasAnyFreeText) {
      // Composite labelled display (History Partnership / sleep / alcohol, etc.)
      // — italicise free text in place rather than re-bulleting the whole block.
      const injected = renderDisplayTextWithItalicFreeText(safeDisplayText, enriched);
      if (injected) return injected;

      return (
        <>
          {enriched.map((item, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && '\n'}
              {'• '}
              {renderFormattedAnswerItem(item)}
            </React.Fragment>
          ))}
        </>
      );
    }

    // No free text found (existing or recovered) and items were originally
    // plain strings — fall back to displayText which may contain richer
    // context from PrefillFunctions (e.g. multi-var concatenation)
    const hasOriginalStructuredItems = rawAnswer.some(
      (item) => typeof item === 'object' && item !== null
    );
    if (!hasOriginalStructuredItems) {
      return <>{safeDisplayText}</>;
    }
  }

  // Items that were originally plain strings with no recoverable free text
  // should fall back to displayText for richer PrefillFunctions context
  const hasOriginalStructuredItems = rawAnswer.some(
    (item) => typeof item === 'object' && item !== null
  );
  if (!hasOriginalStructuredItems && !normalizedItems.some((item) => item.otherValue)) {
    return <>{safeDisplayText}</>;
  }

  // Render structured items with bullets and italic-quoted free text
  const itemsToRender = allNormalized ? normalizedItems : rawAnswer;
  return (
    <>
      {itemsToRender.map((item, idx) => (
        <React.Fragment key={idx}>
          {idx > 0 && '\n'}
          {'• '}
          {renderFormattedAnswerItem(item)}
        </React.Fragment>
      ))}
    </>
  );
};
